import { useEffect, useMemo } from 'react';

import orderBy from 'lodash/orderBy';
import { Form } from 'react-final-form';
import * as Yup from 'yup';

import { countryCodeMap, IS_PROD, type CountryCodeMap } from '@cast/constants';
import {
  ActionsGroup,
  Button,
  DialogV2,
  Icons,
  Inset,
  Stack,
  Typography,
  makeDialog,
  notify,
  tokens,
  useCloseDialog,
} from '@cast/design-system';
import { useUser } from '@cast/infrastructure-auth';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { makeValidateSync } from '@cast/utils';

import { useCountryCodeQuery } from 'hooks/queries';

import { ERROR_TOAST, SUCCESS_TOAST, splitDisplayName } from './_utils';
import { useSubmitReportingContactSalesMutation } from './useSubmitReportingContactSalesMutation';

import { RffInput, RffSelect } from '@cast/shared-rff-ui';

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  countryCode?: string;
  title?: string;
};

export type ReportingContactSalesDialogProps = {
  onSuccess?: () => void;
};

const schema = Yup.object({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  email: Yup.string()
    .trim()
    .email('Enter a valid email')
    .required('Work email is required'),
  company: Yup.string().trim().required('Company is required'),
  countryCode: Yup.string().trim().optional(),
  title: Yup.string().trim().optional(),
});

const validate = makeValidateSync(schema);

const countryOptions = orderBy(
  countryCodeMap,
  [(o) => o.orderWeight || 0, 'label'],
  ['desc', 'asc']
);

const RequiredLabel = ({ children }: { children: string }) => (
  <>
    {children}{' '}
    <Typography
      component="span"
      variant="P12M"
      color={tokens.theme.fg.status.error}
    >
      *
    </Typography>
  </>
);

const ContactSalesFormBody = ({
  handleSubmit,
  isPending,
  ipCountryCode,
  setCountryCode,
  closeDialog,
}: {
  handleSubmit: () => void;
  isPending: boolean;
  ipCountryCode?: string;
  setCountryCode: (code: string) => void;
  closeDialog: () => void;
}) => {
  useEffect(() => {
    if (!ipCountryCode) {
      return;
    }
    const match = countryOptions.find((c) => c.code === ipCountryCode);
    if (match) {
      setCountryCode(match.code);
    }
    // Prefill once when IP country resolves
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipCountryCode]);

  return (
    <DialogV2
      testId="reporting-contact-sales-dialog"
      title="Contact sales"
      footerDivider
      slotProps={{ root: { size: 480 } }}
      startActions={
        <Button
          variant="ghost"
          size="sm"
          startIcon={<Icons.ArrowLeft />}
          onClick={() => closeDialog()}
          testId="reporting-contact-sales-back"
        >
          Back
        </Button>
      }
      endActions={
        <ActionsGroup>
          <Button
            variant="primary"
            size="sm"
            loading={isPending}
            onClick={handleSubmit}
            testId="reporting-contact-sales-submit"
          >
            Send request
          </Button>
        </ActionsGroup>
      }
    >
      <Inset pb={8}>
        <Stack gap={16} component="form" onSubmit={handleSubmit}>
          <Typography variant="P14R">
            We&apos;ve pre-filled what we know. Review your details, add
            anything that&apos;s missing, and our team will get back to you.
          </Typography>

          <RffInput
            name="firstName"
            size="medium"
            label={<RequiredLabel>First name</RequiredLabel>}
            testId="reporting-contact-sales-first-name"
          />
          <RffInput
            name="lastName"
            size="medium"
            label={<RequiredLabel>Last name</RequiredLabel>}
            testId="reporting-contact-sales-last-name"
          />
          <RffInput
            name="email"
            size="medium"
            label={<RequiredLabel>Work email</RequiredLabel>}
            testId="reporting-contact-sales-email"
          />
          <RffSelect<CountryCodeMap>
            name="countryCode"
            size="md"
            label="Country"
            options={countryOptions}
            optionLabel="label"
            optionValue="code"
            inputProps={{ placeholder: 'Select country' }}
            testId="reporting-contact-sales-country"
          />
          <RffInput
            name="title"
            size="medium"
            label="Job title"
            testId="reporting-contact-sales-title"
          />
          <RffInput
            name="company"
            size="medium"
            label={<RequiredLabel>Company</RequiredLabel>}
            testId="reporting-contact-sales-company"
          />
        </Stack>
      </Inset>
    </DialogV2>
  );
};

const ReportingContactSalesDialogContent = ({
  onSuccess,
}: ReportingContactSalesDialogProps) => {
  const closeDialog = useCloseDialog();
  const user = useUser();
  const { currentOrganization } = useOrganizations();
  const { country: ipCountryCode } = useCountryCodeQuery(IS_PROD);
  const { mutate, isPending } = useSubmitReportingContactSalesMutation();

  const nameParts = splitDisplayName(user.name, user.firstName);

  const initialValues = useMemo<FormValues>(
    () => ({
      firstName: nameParts.firstName,
      lastName: nameParts.lastName,
      email: user.email ?? '',
      company: currentOrganization?.name ?? '',
      countryCode: undefined,
      title: '',
    }),
    [
      currentOrganization?.name,
      nameParts.firstName,
      nameParts.lastName,
      user.email,
    ]
  );

  return (
    <Form<FormValues>
      initialValues={initialValues}
      validate={validate}
      onSubmit={(values) => {
        if (!currentOrganization?.id) {
          notify.error(ERROR_TOAST);
          return;
        }

        const country = countryOptions.find(
          (c) => c.code === values.countryCode
        );

        mutate(
          {
            email: values.email.trim(),
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            company: values.company.trim(),
            country: country?.label,
            title: values.title?.trim() || undefined,
            orgId: currentOrganization.id,
            orgName: currentOrganization.name ?? values.company.trim(),
          },
          {
            onSuccess: () => {
              notify.success(SUCCESS_TOAST);
              onSuccess?.();
              closeDialog();
            },
            onError: () => {
              notify.error(ERROR_TOAST);
            },
          }
        );
      }}
      render={({ handleSubmit, form }) => (
        <ContactSalesFormBody
          handleSubmit={handleSubmit}
          isPending={isPending}
          ipCountryCode={ipCountryCode}
          setCountryCode={(code: string) => form.change('countryCode', code)}
          closeDialog={closeDialog}
        />
      )}
    />
  );
};

const useReportingContactSalesDialogBase = makeDialog(
  ReportingContactSalesDialogContent,
  { closeOnOverlayClick: true }
);

export const useReportingContactSalesDialog = () => {
  const { openDialog } = useReportingContactSalesDialogBase();

  return {
    openReportingContactSales: (args?: ReportingContactSalesDialogProps) =>
      openDialog(args ?? {}),
  };
};
